import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { userLsKey } from '@/lib/userScopedStorage';

const BASE_KEY = 'calendar-hidden-sources';
const LEGACY_KEY = BASE_KEY;
const SYNC_EVENT = 'calendarSourceFilterChanged';

function loadSources(userId: string | null): Set<string> {
  try {
    const key = userLsKey(BASE_KEY, userId);
    const raw = localStorage.getItem(key);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {}
  return new Set<string>();
}

function persistSources(sources: Set<string>, userId: string | null): void {
  const key = userLsKey(BASE_KEY, userId);
  localStorage.setItem(key, JSON.stringify([...sources]));
  window.dispatchEvent(new CustomEvent(SYNC_EVENT));
}

export function useCalendarSourceFilter() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const userIdRef = useRef<string | null>(userId);

  // Initialize empty; populated once userId is known.
  const [hiddenSources, setHiddenSources] = useState<Set<string>>(() => new Set());

  // Load from user-scoped key when userId resolves; clean up legacy unscoped key.
  useEffect(() => {
    userIdRef.current = userId;
    setHiddenSources(loadSources(userId));
    if (userId) {
      try { localStorage.removeItem(LEGACY_KEY); } catch {}
    }
  }, [userId]);

  // Re-sync when another tab persists a change.
  useEffect(() => {
    const handler = () => setHiddenSources(loadSources(userIdRef.current));
    window.addEventListener(SYNC_EVENT, handler);
    return () => window.removeEventListener(SYNC_EVENT, handler);
  }, []);

  const toggleSource = useCallback((id: string) => {
    setHiddenSources(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      window.setTimeout(() => persistSources(next, userIdRef.current), 0);
      return next;
    });
  }, []);

  const setSourcesVisibility = useCallback((ids: string[], visible: boolean) => {
    setHiddenSources(prev => {
      const next = new Set(prev);
      ids.forEach(id => {
        if (visible) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });
      window.setTimeout(() => persistSources(next, userIdRef.current), 0);
      return next;
    });
  }, []);

  const isVisible = useCallback((id: string) => !hiddenSources.has(id), [hiddenSources]);

  return { hiddenSources, toggleSource, setSourcesVisibility, isVisible };
}
